/**
 * © 2026 iTech India Private Limited. All rights reserved.
 * Licensed under the iTech End User License Agreement (EULA).
 * Unauthorized use or distribution is prohibited.
 */

import { type ChangeEvent, type JSX, type ReactNode, useCallback, useMemo } from 'react';
import { type Control, type FieldPath, type FieldValues, useController } from 'react-hook-form';

import { cn } from '../../lib/utils/cn.js';

export interface Option {
  readonly value: string;
  readonly label: string;
}

interface BaseProps<T extends FieldValues> {
  readonly control: Control<T>;
  readonly name: FieldPath<T>;
  readonly label: string;
  readonly hint?: string | undefined;
}

interface OptionsProps<T extends FieldValues> extends BaseProps<T> {
  readonly options: readonly Option[];
}

const INPUT_CLASS =
  'focus-ring w-full rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-white';
const LABEL_CLASS = 'mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-300';
const PILL_BASE =
  'focus-ring rounded-xl border px-4 py-2 text-sm font-medium transition-colors dark:border-slate-700';
const PILL_ON = 'border-[var(--accent)] bg-[var(--accent)] text-white';
const PILL_OFF = 'border-slate-200 bg-white text-slate-600 dark:bg-slate-800 dark:text-slate-300';

/** Label + hint + error wrapper shared by every intake field. */
function FieldShell({
  htmlFor,
  label,
  hint,
  error,
  children,
}: {
  readonly htmlFor?: string | undefined;
  readonly label: string;
  readonly hint?: string | undefined;
  readonly error?: string | undefined;
  readonly children: ReactNode;
}): JSX.Element {
  return (
    <div>
      <label htmlFor={htmlFor} className={LABEL_CLASS}>
        {label}
      </label>
      {hint !== undefined && <p className="-mt-1 mb-1.5 text-xs text-slate-400">{hint}</p>}
      {children}
      {error !== undefined && (
        <p role="alert" className="mt-1 text-xs text-red-600 dark:text-red-400">
          {error}
        </p>
      )}
    </div>
  );
}

/** Single-line text / number / date input. */
export function TextField<T extends FieldValues>({
  control,
  name,
  label,
  hint,
  type = 'text',
  placeholder,
}: BaseProps<T> & { readonly type?: string; readonly placeholder?: string }): JSX.Element {
  const { field, fieldState } = useController({ control, name });
  return (
    <FieldShell htmlFor={name} label={label} hint={hint} error={fieldState.error?.message}>
      <input
        id={name}
        type={type}
        placeholder={placeholder}
        className={INPUT_CLASS}
        aria-invalid={fieldState.error !== undefined}
        value={
          typeof field.value === 'number' || typeof field.value === 'string' ? field.value : ''
        }
        onChange={field.onChange}
        onBlur={field.onBlur}
      />
    </FieldShell>
  );
}

/** Multi-line text area. */
export function TextareaField<T extends FieldValues>({
  control,
  name,
  label,
  hint,
  placeholder,
}: BaseProps<T> & { readonly placeholder?: string }): JSX.Element {
  const { field, fieldState } = useController({ control, name });
  return (
    <FieldShell htmlFor={name} label={label} hint={hint} error={fieldState.error?.message}>
      <textarea
        id={name}
        rows={3}
        placeholder={placeholder}
        className={INPUT_CLASS}
        aria-invalid={fieldState.error !== undefined}
        value={typeof field.value === 'string' ? field.value : ''}
        onChange={field.onChange}
        onBlur={field.onBlur}
      />
    </FieldShell>
  );
}

/** Native dropdown. */
export function SelectField<T extends FieldValues>({
  control,
  name,
  label,
  hint,
  options,
  placeholder,
}: OptionsProps<T> & { readonly placeholder: string }): JSX.Element {
  const { field, fieldState } = useController({ control, name });
  return (
    <FieldShell htmlFor={name} label={label} hint={hint} error={fieldState.error?.message}>
      <select
        id={name}
        className={INPUT_CLASS}
        aria-invalid={fieldState.error !== undefined}
        value={typeof field.value === 'string' ? field.value : ''}
        onChange={field.onChange}
        onBlur={field.onBlur}
      >
        <option value="" disabled>
          {placeholder}
        </option>
        {options.map((o) => (
          <option key={o.value} value={o.value}>
            {o.label}
          </option>
        ))}
      </select>
    </FieldShell>
  );
}

/** One selectable pill (extracted so the click handler is a stable callback). */
function Pill({
  value,
  label,
  active,
  onSelect,
}: {
  readonly value: string;
  readonly label: string;
  readonly active: boolean;
  readonly onSelect: (value: string) => void;
}): JSX.Element {
  const handle = useCallback(() => {
    onSelect(value);
  }, [onSelect, value]);
  return (
    <button
      type="button"
      className={cn(PILL_BASE, active ? PILL_ON : PILL_OFF)}
      aria-pressed={active}
      onClick={handle}
    >
      {label}
    </button>
  );
}

/** Single-choice pill group (Yes/No/Sometimes and radio-style option sets). */
export function ChoiceField<T extends FieldValues>({
  control,
  name,
  label,
  hint,
  options,
}: OptionsProps<T>): JSX.Element {
  const { field, fieldState } = useController({ control, name });
  const onSelect = useCallback(
    (value: string) => {
      field.onChange(value);
    },
    [field],
  );
  return (
    <FieldShell label={label} hint={hint} error={fieldState.error?.message}>
      <div className="flex flex-wrap gap-2">
        {options.map((o) => (
          <Pill
            key={o.value}
            value={o.value}
            label={o.label}
            active={field.value === o.value}
            onSelect={onSelect}
          />
        ))}
      </div>
    </FieldShell>
  );
}

/** Multi-select pill list backed by a string array (e.g. food textures). */
export function MultiChoiceField<T extends FieldValues>({
  control,
  name,
  label,
  hint,
  options,
}: OptionsProps<T>): JSX.Element {
  const { field, fieldState } = useController({ control, name });
  const selected = useMemo<readonly string[]>(
    () => (Array.isArray(field.value) ? (field.value as string[]) : []),
    [field.value],
  );
  const onToggle = useCallback(
    (value: string) => {
      field.onChange(
        selected.includes(value) ? selected.filter((v) => v !== value) : [...selected, value],
      );
    },
    [field, selected],
  );
  return (
    <FieldShell label={label} hint={hint} error={fieldState.error?.message}>
      <div className="flex flex-wrap gap-2">
        {options.map((o) => (
          <Pill
            key={o.value}
            value={o.value}
            label={o.label}
            active={selected.includes(o.value)}
            onSelect={onToggle}
          />
        ))}
      </div>
    </FieldShell>
  );
}

/** One checkbox in a skill grid (extracted for a stable change handler). */
function GridItem({
  value,
  label,
  checked,
  onToggle,
}: {
  readonly value: string;
  readonly label: string;
  readonly checked: boolean;
  readonly onToggle: (value: string, checked: boolean) => void;
}): JSX.Element {
  const handle = useCallback(
    (e: ChangeEvent<HTMLInputElement>) => {
      onToggle(value, e.target.checked);
    },
    [onToggle, value],
  );
  return (
    <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm dark:border-slate-700 dark:bg-slate-800 dark:text-slate-300">
      <input
        type="checkbox"
        className="focus-ring h-4 w-4 rounded"
        checked={checked}
        onChange={handle}
      />
      {label}
    </label>
  );
}

/** Per-skill checkbox grid backed by a string-to-boolean record (gross/fine motor). */
export function CheckboxGridField<T extends FieldValues>({
  control,
  name,
  label,
  hint,
  options,
}: OptionsProps<T>): JSX.Element {
  const { field, fieldState } = useController({ control, name });
  const map = useMemo<Record<string, boolean>>(
    () =>
      typeof field.value === 'object' && field.value !== null
        ? (field.value as Record<string, boolean>)
        : {},
    [field.value],
  );
  const onToggle = useCallback(
    (value: string, checked: boolean) => {
      field.onChange({ ...map, [value]: checked });
    },
    [field, map],
  );
  return (
    <FieldShell label={label} hint={hint} error={fieldState.error?.message}>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        {options.map((o) => (
          <GridItem
            key={o.value}
            value={o.value}
            label={o.label}
            checked={map[o.value] === true}
            onToggle={onToggle}
          />
        ))}
      </div>
    </FieldShell>
  );
}
